import { spaces } from '@kimuno/theme';
import { InsertLink } from '@mui/icons-material';
import { Box, Button, TextField } from '@mui/material';
import type React from 'react';
import { memo, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

type LinkEditorPanelProps = {
	initialUrl?: string;
	onSubmit: ({ url }: { url: string }) => void;
};

const URL_REGEX =
	/(https:\/\/www\.|http:\/\/www\.|https:\/\/|http:\/\/)?[a-zA-Z]{2,}(\.[a-zA-Z]{2,})(\.[a-zA-Z]{2,})?\/[a-zA-Z0-9]{2,}|((https:\/\/www\.|http:\/\/www\.|https:\/\/|http:\/\/)?[a-zA-Z]{2,}(\.[a-zA-Z]{2,})(\.[a-zA-Z]{2,})?)|(https:\/\/www\.|http:\/\/www\.|https:\/\/|http:\/\/)?[a-zA-Z0-9]{2,}\.[a-zA-Z0-9]{2,}\.[a-zA-Z0-9]{2,}(\.[a-zA-Z0-9]{2,})?/;

const LinkEditorPanel = ({ initialUrl = '', onSubmit }: LinkEditorPanelProps) => {
	const { t } = useTranslation();
	const [url, setUrl] = useState(initialUrl);
	const [isUrlValid, setIsUrlValid] = useState(true);

	const handleUrlChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
		const newUrl = event.target.value;
		setUrl(newUrl);

		setIsUrlValid(URL_REGEX.test(newUrl));
	}, []);

	const handleSubmit = useCallback(() => {
		if (isUrlValid) {
			onSubmit({ url });
		}
	}, [url, onSubmit, isUrlValid]);

	return (
		<Box sx={{ p: spaces.xs }} data-testid="link-editor-panel">
			<Box
				sx={{
					display: 'flex',
					flexDirection: 'row',
					gap: spaces.xxs,
					alignItems: 'center',
				}}
			>
				<TextField
					data-testid="link-url-input"
					type="url"
					size="small"
					id="link-url-input"
					variant="outlined"
					placeholder={t('Link URL')}
					value={url}
					onChange={handleUrlChange}
					fullWidth
					InputProps={{
						startAdornment: <InsertLink fontSize="small" sx={{ mr: spaces.xxs }} />,
					}}
				/>
				<Button
					data-testid="submit-link"
					variant="contained"
					onClick={handleSubmit}
					sx={{ whiteSpace: 'nowrap' }}
					disabled={!isUrlValid || !url}
				>
					{t('Set Link')}
				</Button>
			</Box>
		</Box>
	);
};

export default memo(LinkEditorPanel);
